import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { TablePagination } from '../../../../components/design-system/admin';
import { Vendor } from '../models/vendor.model';
import {
    ROWS_PER_PAGE_OPTIONS,
    UsageValue,
    VendorActionsMenu,
    VendorCollectionProps,
    VendorLogo,
    VendorStatus,
    VisualizationsWithChange,
    openVendorVisualizer,
    usageOf,
} from './vendor-row-parts.component';

const SKELETON_CARDS = 8;

/** One vendor as a card: logo, who they are, plan and status, the last 30 days of visualizations. */
function VendorCard({ vendor, props }: { vendor: Vendor; props: VendorCollectionProps }) {
    const { t } = useTranslation();
    const row = usageOf(props.usage, vendor);
    const editHref = `/vendors/${vendor._id}/edit`;

    return (
        <Card className="h-full gap-0 py-0" data-testid="vendor-card">
            <Link
                to={editHref}
                tabIndex={-1}
                aria-hidden
                className="flex h-32 items-center justify-center border-b bg-muted/30 p-5"
            >
                <VendorLogo vendor={vendor} className="size-full border-0 bg-transparent" />
            </Link>

            <div className="flex flex-1 flex-col gap-3 p-4">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <Link to={editHref} className="block truncate font-medium hover:underline">
                            {vendor.name}
                        </Link>
                        <p className="truncate text-xs text-muted-foreground">@{vendor.alias}</p>
                    </div>
                    <VendorActionsMenu
                        vendor={vendor}
                        onEdit={props.handleEditVendor}
                        onToggleStatus={props.onToggleStatus}
                        onDelete={props.onDelete}
                        onMoveToProspects={props.onMoveToProspects}
                    />
                </div>

                <div className="flex items-center justify-between gap-2">
                    <VendorStatus vendor={vendor} />
                    <span className="truncate text-xs text-muted-foreground">
                        <UsageValue usage={props.usage}>{row?.planName || '—'}</UsageValue>
                    </span>
                </div>

                <div className="mt-auto flex items-end justify-between gap-2 border-t pt-3">
                    <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">{t('ADMIN_VENDORS.GRID.VISUALIZATIONS')}</p>
                        <div className="mt-0.5 text-base font-medium">
                            <UsageValue usage={props.usage}>
                                <VisualizationsWithChange row={row} />
                            </UsageValue>
                        </div>
                    </div>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="outline"
                                size="icon-sm"
                                aria-label={t('ADMIN_SHELL.COMMON.OPEN_VISUALIZER')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openVendorVisualizer(vendor);
                                }}
                            >
                                <ExternalLink className="size-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('ADMIN_SHELL.COMMON.OPEN_VISUALIZER')}</TooltipContent>
                    </Tooltip>
                </div>
            </div>
        </Card>
    );
}

/** The vendors as cards, with the same footer as the table. */
export const VendorsAsGrid = (props: VendorCollectionProps) => {
    const { vendors, pagination, loading } = props;
    const firstLoad = loading && vendors.length === 0;

    return (
        <div className="flex flex-col gap-4" aria-busy={loading}>
            <div
                className={cn(
                    'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
                    loading && !firstLoad && 'opacity-60 transition-opacity',
                )}
                data-testid="vendors-grid"
            >
                {firstLoad
                    ? Array.from({ length: SKELETON_CARDS }).map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)
                    : vendors.map((vendor, index) => <VendorCard key={vendor._id || index} vendor={vendor} props={props} />)}
            </div>

            <TablePagination
                page={Math.max(0, (pagination?.page || 1) - 1)}
                pageSize={pagination?.limit || 10}
                total={pagination?.total || 0}
                onPageChange={props.onPageChange}
                onPageSizeChange={props.onRowsPerPageChange}
                pageSizes={ROWS_PER_PAGE_OPTIONS}
            />
        </div>
    );
};
